import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { isMobile } from "react-device-detect";
import { Field, FieldProps, Formik } from "formik";
import * as Yup from "yup";
import DaumPostcode, { Address } from "react-daum-postcode";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import { ShippingInfo } from "@/features/my-account/types/shipping-info.type";

interface ShippingInfoFormValues {
  alias: string;
  recipientName: string;
  contact: string;
  address: string;
  detailAddress: string;
  zipCode: string;
  isDefault: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: ShippingInfoFormValues) => Promise<void>;
  initialValues?: Partial<ShippingInfo>;
  title: string;
}

export default function ShippingInfoModal({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  title,
}: Props) {
  const [showPostcode, setShowPostcode] = useState(false);

  const formInitialValues = useMemo(
    (): ShippingInfoFormValues => ({
      alias: initialValues?.alias ?? "",
      recipientName: initialValues?.recipientName ?? "",
      contact: initialValues?.contact ?? "",
      address: initialValues?.address ?? "",
      detailAddress: initialValues?.detailAddress ?? "",
      zipCode: initialValues?.zipCode ?? "",
      isDefault: initialValues?.isDefault ?? false,
    }),
    [initialValues]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        alias: Yup.string().required("별칭을 입력해주세요."),
        recipientName: Yup.string().required("이름을 입력해주세요."),
        contact: Yup.string().required("연락처를 입력해주세요."),
        address: Yup.string().required("주소를 입력해주세요."),
        zipCode: Yup.string().required("우편번호를 입력해주세요."),
      }),
    []
  );

  const handleAddressSelect = useCallback(
    (data: Address, setFieldValue: (field: string, value: string) => void) => {
      setFieldValue("address", data.address);
      setFieldValue("zipCode", data.zonecode);
      setShowPostcode(false);
    },
    []
  );

  return (
    // allowPinchZoom: 이 모달의 입력 필드에서 화면이 확대됐을 때 두 손가락 축소로
    // 빠져나올 수 있게 스크롤 락의 핀치 제스처 차단을 푼다. 아래 DaumPostcode는
    // iframe이라 우리 폰트 규칙이 닿지 않으므로 여기서는 핀치가 유일한 탈출 경로다.
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      allowPinchZoom={isMobile}
    >
      <ModalOverlay />
      {/* 모달 컨테이너에 좌우 여백이 없어, 480px보다 좁은 화면에서는 모달이 양 끝에
          붙는다. 모바일에서만 화면 폭에서 여백만큼 뺀 값으로 잡는다. */}
      <ModalContent
        borderRadius={"8px"}
        overflow={"hidden"}
        maxWidth={{ base: "calc(100% - 32px)", md: "480px" }}
      >
        <ModalBody padding={"0"}>
          <Formik<ShippingInfoFormValues>
            enableReinitialize={true}
            initialValues={formInitialValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (values, actions) => {
              await onSubmit(values);
              actions.setSubmitting(false);
            }}
          >
            {(formik) => (
              <Grid
                as={"form"}
                gridTemplateRows={"auto 1fr"}
                height={"100%"}
                onSubmit={formik.handleSubmit as any}
              >
                <Flex
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  borderBottom={"0.5px solid #D4D4D1"}
                  padding={"16px 24px"}
                >
                  <Heading as={"h2"} fontSize={"14px"}>
                    {title}
                  </Heading>
                  <Flex gap={"8px"}>
                    <AppButton isDisabled={formik.isSubmitting} onClick={onClose}>
                      취소
                    </AppButton>
                    <AppButton
                      variant={"primary"}
                      isLoading={formik.isSubmitting}
                      type={"submit"}
                    >
                      저장
                    </AppButton>
                  </Flex>
                </Flex>

                <Flex flexDirection={"column"} gap={"12px"} padding={"24px"}>
                  <Field name={"alias"}>
                    {(props: FieldProps<string>) => (
                      <FormControl isRequired>
                        <FormLabel fontSize={"12px"} color={"#6F6F6C"} mb={"4px"}>
                          별칭
                        </FormLabel>
                        <Input
                          {...props.field}
                          fontSize={"12px"}
                          height={"28px"}
                          placeholder={"예) 우리 집"}
                        />
                        {formik.errors.alias && (
                          <Text fontSize={"11px"} color={"red.400"} mt={"2px"}>
                            {formik.errors.alias}
                          </Text>
                        )}
                      </FormControl>
                    )}
                  </Field>

                  <Field name={"recipientName"}>
                    {(props: FieldProps<string>) => (
                      <FormControl isRequired>
                        <FormLabel fontSize={"12px"} color={"#6F6F6C"} mb={"4px"}>
                          이름
                        </FormLabel>
                        <Input
                          {...props.field}
                          fontSize={"12px"}
                          height={"28px"}
                        />
                        {formik.errors.recipientName && (
                          <Text fontSize={"11px"} color={"red.400"} mt={"2px"}>
                            {formik.errors.recipientName}
                          </Text>
                        )}
                      </FormControl>
                    )}
                  </Field>

                  <Field name={"contact"}>
                    {(props: FieldProps<string>) => (
                      <FormControl isRequired>
                        <FormLabel fontSize={"12px"} color={"#6F6F6C"} mb={"4px"}>
                          연락처
                        </FormLabel>
                        <Input
                          {...props.field}
                          fontSize={"12px"}
                          height={"28px"}
                          placeholder={"010-0000-0000"}
                        />
                        {formik.errors.contact && (
                          <Text fontSize={"11px"} color={"red.400"} mt={"2px"}>
                            {formik.errors.contact}
                          </Text>
                        )}
                      </FormControl>
                    )}
                  </Field>

                  <FormControl isRequired>
                    <FormLabel fontSize={"12px"} color={"#6F6F6C"} mb={"4px"}>
                      주소
                    </FormLabel>
                    <Flex gap={"8px"} mb={"6px"}>
                      <Input
                        value={formik.values.zipCode}
                        readOnly
                        fontSize={"12px"}
                        height={"28px"}
                        width={"120px"}
                        placeholder={"우편번호"}
                        cursor={"default"}
                      />
                      <AppButton
                        size={"small"}
                        onClick={() => setShowPostcode((v) => !v)}
                      >
                        주소 검색
                      </AppButton>
                    </Flex>
                    {showPostcode && (
                      <Box
                        border={"1px solid #D4D4D1"}
                        borderRadius={"4px"}
                        mb={"6px"}
                        overflow={"hidden"}
                      >
                        <DaumPostcode
                          onComplete={(data) =>
                            handleAddressSelect(data, formik.setFieldValue)
                          }
                          style={{ height: "320px" }}
                        />
                      </Box>
                    )}
                    <Input
                      value={formik.values.address}
                      readOnly
                      fontSize={"12px"}
                      height={"28px"}
                      cursor={"default"}
                      placeholder={"주소 검색 버튼을 눌러주세요"}
                      mb={"6px"}
                    />
                    {formik.errors.address && (
                      <Text fontSize={"11px"} color={"red.400"} mb={"4px"}>
                        {formik.errors.address}
                      </Text>
                    )}
                    <Field name={"detailAddress"}>
                      {(props: FieldProps<string>) => (
                        <Input
                          {...props.field}
                          fontSize={"12px"}
                          height={"28px"}
                          placeholder={"상세 주소"}
                        />
                      )}
                    </Field>
                  </FormControl>

                  <Field name={"isDefault"}>
                    {(props: FieldProps<boolean>) => (
                      <FormControl>
                        <AppCheckBox
                          isChecked={props.field.value}
                          name={"기본 배송지로 설정"}
                          onChange={(value) =>
                            formik.setFieldValue("isDefault", value)
                          }
                        />
                      </FormControl>
                    )}
                  </Field>
                </Flex>
              </Grid>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
